import { useEffect, useState, useCallback } from 'react';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourOverlayProps {
  targetId: string;
  padding?: number;
  onRectChange?: (rect: SpotlightRect) => void;
  onDismiss?: () => void;
}

function getTargetRect(targetId: string, padding: number): SpotlightRect | null {
  const el = document.getElementById(targetId);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

export const TourOverlay = ({
  targetId,
  padding = 8,
  onRectChange,
  onDismiss,
}: TourOverlayProps) => {
  const [rect, setRect] = useState<SpotlightRect | null>(null);

  const updateRect = useCallback(() => {
    const newRect = getTargetRect(targetId, padding);
    if (newRect) {
      setRect(newRect);
      onRectChange?.(newRect);
    }
  }, [targetId, padding, onRectChange]);

  useEffect(() => {
    updateRect();

    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(updateRect, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, [updateRect]);

  if (!rect) return null;

  return (
    <>
      {/* Full-screen backdrop that blocks interaction */}
      <div
        className="fixed inset-0 z-overlay"
        onClick={onDismiss}
        aria-hidden="true"
      />
      {/* Spotlight cutout using box-shadow technique */}
      <div
        className="fixed z-overlay rounded-2xl transition-all duration-300"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />
    </>
  );
};
